import { toHumanReadableTitle } from "components/athena/CsvColumnNameParsing";
import { ToggleGroups } from "components/GroupedToggleList";

export const mapToToggleGroups = (dataFields: {
  [group: string]: { [name: string]: string };
}): ToggleGroups => {
  return Object.assign(
    {},
    ...Object.entries(dataFields).map(([groupName, fields]) => ({
      [groupName]: Object.entries(fields).map(([fieldName]) => ({
        title: fieldName,
        name: fieldName,
        text: toHumanReadableTitle(fieldName),
      })),
    }))
  );
};

export const packDataFields = mapToToggleGroups({
  Temperature: {
    cell_temperature_max_c: "",
    cell_temperature_min_c: "",
    cell_temperature_median_c: "",
    ambient_temperature_c: "",
  },
  "Battery State Estimation": {
    pack_soc_kwh_pct: "",
    pack_soh_ah_pct: "",
  },
  "Grid data": {
    grid_frequency_hz: "",
    grid_voltage_v: "",
    grid_current_a: "",
    power_factor: "",
    power_va_kva: "",
    power_var_kvar: "",
  },
  "Battery data": {
    battery_current_a: "",
    battery_voltage_v: "",
  },
  "Cell data": {
    cell_voltage_max_v: "",
    cell_voltage_min_v: "",
    cell_voltage_median_v: "",
    cell_soc_max_pct: "",
    cell_soc_min_pct: "",
    cell_soc_median_pct: "",
  },
});

export const cellDataFields = mapToToggleGroups({
  Temperature: {
    cell_temperature_c: "",
    switch_temperature_c: "",
  },
  "Cell State Estimation": {
    cell_soc_pct: "",
    cell_soh_pct: "",
  },
  "Other data": {
    switch_resistance_mohm: "",
  },
  "Cell data": {
    cell_voltage_v: "",
    bridge_voltage_v: "",
    cell_current_a: "",
    cell_rank: "",
    cell_resistance_ohmic_mohm: "",
    cell_resistance_dc_mohm: "",
  },
});

export const unitDataFields = mapToToggleGroups({
  Grid: {
    grid_voltage_v: "",
    grid_current_a: "",
    grid_frequency_hz: "",
    grid_p_kw: "",
    grid_q_kvar: "",
    grid_s_kva: "",
    grid_pf: "",
  },
  Internal: {
    internal_voltage_v: "",
    internal_current_a: "",
    internal_frequency_hz: "",
    internal_p_kw: "",
    internal_q_kvar: "",
    internal_s_kva: "",
    internal_pf: "",
  },
  Meter: {
    ext_meter_voltage_v: "",
    ext_meter_current_a: "",
    ext_meter_frequency_hz: "",
    ext_meter_p_kw: "",
    ext_meter_q_kvar: "",
    ext_meter_s_kva: "",
    ext_meter_pf: "",
  },
});

export const unitStateFields = mapToToggleGroups({
  State: {
    "state['ESDlyRemTms']": "",
    "state['es']": "",
    "state['stateCoreFsm']": "",
    "state['stateCoreFsmOn']": "",
    "state['stateEsFsm']": "",
    "state['stateGridConnectFsm']": "",
  },
  "Disconnect Flags": {
    "flag['fDisconnectDrm']": "",
    "flag['fDisconnectOf']": "",
    "flag['fDisconnectOvA']": "",
    "flag['fDisconnectOvB']": "",
    "flag['fDisconnectOvSO_p']": "",
    "flag['fDisconnectOvSO_p2']": "",
    "flag['fDisconnectOvSO_p3']": "",
    "flag['fDisconnectPB']": "",
    "flag['fDisconnectRuc']": "",
    "flag['fDisconnectUf']": "",
    "flag['fDisconnectUvA']": "",
    "flag['fDisconnectUvB']": "",
    "flag['fDisconnectph2ph']": "",
  },
  "Fault Flags": {
    "flag['fFaultEL']": "",
    "flag['fFaultExt']": "",
    "flag['fFaultGL']": "",
    "flag['fFaultRuc']": "",
  },
  "Relay Flags": {
    "flag['fRelayClosedEps']": "",
    "flag['fRelayClosedGrid']": "",
    "flag['fRelayReqEps']": "",
    "flag['fRelayReqGrid']": "",
    "flag['fReqEpsConnectUsr']": "",
    "flag['fReqGridConnectUsr']": "",
    "flag['fReqGridFormingMode']": "",
  },
  "Other Flags": {
    "flag['fCessationOv']": "",
    "flag['fCessationPB']": "",
    "flag['fCessationUv']": "",
    "flag['fThrottleCrlA']": "",
    "flag['fThrottleCrlB']": "",
    "flag['fThrottleCrlC']": "",
    "flag['fEventFreqResponse']": "",
  },
  "Power Targets": {
    "target['P_correction_K']": "",
    "target['P_debug']": "",
    "target['P_overlimit']": "",
    "target['P_req_DRM']": "",
    "target['P_req_PQRM']": "",
    "target['P_req_after_egl']": "",
    "target['P_req_usr']": "",
    "target['Q_correction_K']": "",
    "target['Q_debug']": "",
    "target['Q_req_DRM']": "",
    "target['Q_req_PQRM']": "",
    "target['Q_req_after_egl']": "",
    "target['Q_req_usr']": "",
    "target['S_overlimit']": "",
  },
  "Device Targets": {
    "target['id_device_a1']": "",
    "target['id_device_a2']": "",
    "target['id_device_b1']": "",
    "target['id_device_b2']": "",
    "target['id_device_c1']": "",
    "target['id_device_c2']": "",
    "target['id_phase_a']": "",
    "target['id_phase_b']": "",
    "target['id_phase_c']": "",
    "target['iq_device_a1']": "",
    "target['iq_device_a2']": "",
    "target['iq_device_b1']": "",
    "target['iq_device_b2']": "",
    "target['iq_device_c1']": "",
    "target['iq_device_c2']": "",
    "target['iq_phase_a']": "",
    "target['iq_phase_b']": "",
    "target['iq_phase_c']": "",
  },
  "Other Targets": {
    "target['site_id_a']": "",
    "target['site_id_b']": "",
    "target['site_id_c']": "",
    "target['vAvgOvSOP1']": "",
    "target['vAvgOvSOP2']": "",
    "target['vAvgOvSOP3']": "",
  },
});
